import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-alpine.css'
import 'components/common/SplitPane/splitPaneResizer.css'
import './App.css'
import 'chartjs-adapter-moment'

import { AllEnterpriseModule, LicenseManager, ModuleRegistry, provideGlobalGridOptions } from 'ag-grid-enterprise'
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  TimeScale,
  Title,
  Tooltip,
} from 'chart.js'
import { BoxAndWiskers, BoxPlotController } from '@sgratzl/chartjs-chart-boxplot'
import {
  CenterTextPlugIn,
  ChartBackgroundPlugIn,
  GradientPlugIn,
  chartDatasetSyncPlugin,
  chartMeasurePlugin,
  customAnnotation,
  dataLabelPlugin,
  fillBetweenLinesPlugin,
  shadingAreaPluginX,
} from 'components/common/ChartJsPlugins/chartJsPlugins'
import { CssBaseline, ThemeProvider } from '@mui/material'
import { Suspense, lazy } from 'react'
import { darkTheme, lightTheme } from './theme'
import useInnovaAuth, { ProvideAuth } from 'components/common/hooks/useInnovaAuth'

import ActionBar from './ActionBar/ActionBar'
import { AgChartsEnterpriseModule } from 'ag-charts-enterprise'
import { AppSettingsProvider } from './contexts/AppSettingContext'
import { ChatProvider } from './contexts/ChatContext'
import { CircularProgress } from '@mui/material'
import LoginCallBack from './LandingPage/LoginCallback'
import MainRouter from './router'
import NavBar from './NavBar'
import { ROUTES } from 'components/routes'
import ReactGA from 'react-ga4'
import { RecoilRoot } from 'recoil'
import { StyledEngineProvider } from '@mui/material/styles'
import { WellListProvider } from 'components/common/hooks/WellListContext'
import annotationPlugin from 'chartjs-plugin-annotation'
import innovaLogo from 'assets/innovaLogo.png'
import { useAuth0 } from '@auth0/auth0-react'
import useInnovaTheme from 'components/common/hooks/useInnovaTheme'
import zoomPlugin from 'chartjs-plugin-zoom'

const AnalyticsPage = lazy(() => import('./Analytics/AnalyticsPage/AnalyticsPage'))
const AntiCollisionDashPage = lazy(() => import('components/AntiCollisionDashboard/AntiCollisionDashboardPage'))
const AntiCollisionDashStatusPage = lazy(
  () => import('components/Admin/AntiCollisionDashboardStatus/AntiCollisionDashStatusPage'),
)
const AntiCollisionPage = lazy(() => import('./WellPages/AnticollisionPage/AnticollisionPage'))
const ApiConfigPage = lazy(() => import('./Admin/ApiConfig/ApiConfigPage'))
const AssetSearchPage = lazy(() => import('components/AssetSearch/AssetSearchPage'))
const AuditDataPage = lazy(() => import('./AuditData/AuditDataPage'))
const BankAccountsPage = lazy(() => import('./Admin/InnovaBankAccounts/BankAccountsPage'))
const BidsPage = lazy(() => import('./Bids/BidsPages'))
const BulkEditPage = lazy(() => import('./Admin/BulkEdit/BulkEditPage'))
const CRSPage = lazy(() => import('./CRS/CRSPage'))
const CentralizerCatalogPage = lazy(() => import('./ComponentCatalog/CentralizerCatalogPage'))
const ComponentCatalogPage = lazy(() => import('./ComponentCatalog/ComponentCatalogPage'))
const ConnectionAnalysisPage = lazy(() => import('./WellPages/ConnectionAnalysis/ConnectionAnalysisPage'))
const DailyCostPage = lazy(() => import('./WellPages/DailyCostPage/DailyCostPage'))
const DailyReportsPage = lazy(() => import('./WellPages/DailyReportsPage/DailyReportsPage'))
const InfrastructureDashboardPage = lazy(() => import('./Admin/InfrastructureDashboard/InfrastructureDashboardPage'))
const DataAckFileStatusPage = lazy(() => import('./DataAcquisition/DataAckFileStatus/DataAckFileStatusPage'))
const DataQcPage = lazy(() => import('./DataQc/DataQcPage'))
const DatabaseBackupFilesPage = lazy(() => import('./Admin/DatabaseBackupFiles/DatabaseBackupFilesPage'))
const DatabaseChangesPage = lazy(() => import('./Admin/DatabaseChangesPage/DatabaseChangesPage'))
const DatabaseRestoreStatusPage = lazy(() => import('./Admin/DatabaseRestoreStatus/DatabaseRestoreStatusPage'))
const DatabaseTreePage = lazy(() => import('./DatabaseTree/DatabaseTreePage'))
const DataMigrationPage = lazy(() => import('./DataAcquisition/WellDataSources/WellDataSourcesPage'))
const DrillStringPage = lazy(() => import('./WellPages/DrillStringPages/Dashboard/DrillStringPage'))
const EdrPage = lazy(() => import('./EDR/EdrPage'))
const EndpointUsagePage = lazy(() => import('./Admin/EndpointUsagePage/EndpointUsagePage'))
const EngineeringPage = lazy(() => import('./WellPages/Engineering/EngineeringPage'))
const ErrorModelsPage = lazy(() => import('./ErrorModels/ErrorModelsPage'))
const FileManager = lazy(() => import('./Admin/FileManager/FileManager'))
const IcdsAdminPage = lazy(() => import('./Admin/Icds/IcdsAdminPage'))
const IncidentDashboardPage = lazy(() => import('components/IncidentDashboard/IncidentDashboardPage'))
const InnovaAdminUsersPage = lazy(() => import('./Admin/InnovaAdminUsers/InnovaAdminUsersPage'))
const InnovaCompanysPage = lazy(() => import('./InnovaCompanys/InnovaCompanysPage'))
const InnovaContactsPage = lazy(() => import('./InnovaContacts/InnovaContactsPage'))
const InnovaInvoicesPage = lazy(() => import('./InnovaInvoices/InnovaInvoicesPage'))
const InnovaLicenseDeactivationsPage = lazy(() => import('./InnovaLicenseDeactivations/InnovaLicenseDeactivationsPage'))
const InnovaLicenseRequestPage = lazy(() => import('./InnovaLicenseRequest/InnovaLicenseRequestPage'))
const InnovaLicensingPage = lazy(() => import('./InnovaLicensing/InnovaLicensingPage'))
const InnovaQuotesPage = lazy(() => import('./InnovaQuotes/InnovaQuotesPage'))
const InnovaSoftwareBillingPage = lazy(() => import('./InnovaSoftwareBilling/InnovaSoftwareBillingPage'))
const InnovaSoftwareDetailsPage = lazy(() => import('./InnovaSoftwareDetails/InnovaSoftwareDetailsPage'))
const InnovaVendorsPage = lazy(() => import('./InnovaVendors/InnovaVendorsPage'))
const InventoryPage = lazy(() => import('./WellPages/InventoryPage/InventoryPage'))
const InvoiceQcPage = lazy(() => import('./InvoiceQc/InvoiceQcPage'))
const LandingPage = lazy(() => import('./LandingPage/LandingPage'))
const LogisticsPage = lazy(() => import('components/Logistics/LogisticsPage'))
const MultiDbWellListPage = lazy(() => import('./MultiDbWellList/MultiDbWellListPage'))
const MwdProbeCatalogPage = lazy(() => import('./MwdProbeCatalog/MwdProbeCatalogPage'))
const ObjectValidationPage = lazy(() => import('./ObjectValidation/ObjectValidationPage'))
const WellInsightsPage = lazy(() => import('./WellInsights/WellInsightsPage'))
const OrganizationsPage = lazy(() => import('./Admin/Organizations/OrganizationsPage'))
const PelotonLanding = lazy(() => import('./Peloton/PelotonLanding'))
const PersonnelNamesPage = lazy(() => import('./PersonnelNames/PersonnelNamesPage'))
const PersonnelUtilizationPage = lazy(() => import('./PersonnelUtilization/PersonnelUtilizationPage'))
const PipeTallyPage = lazy(() => import('./WellPages/PipeTallyPage/PipeTallyPage'))
const ResetPasswordPage = lazy(() => import('./LandingPage/ResetPasswordPage'))
const RigNamesPage = lazy(() => import('./RigNames/RigNamesPage'))
const ShippingAddressesPage = lazy(() => import('./ShippingAddresses/ShippingAddressesPage'))
const ShippingTicketPage = lazy(() => import('./WellPages/ShippingTicketsPages/ShippingTicketsPages'))
const StopCollarCatalogPage = lazy(() => import('./ComponentCatalog/StopCollarCatalogPage'))
const SurveysPage = lazy(() => import('./WellPages/SurveyPage/SurveysPage'))
const IntegrationsPage = lazy(() => import('./Integrations/IntegrationsPage'))
const UnitsPage = lazy(() => import('./Admin/Units/UnitsPage'))
const UserDatabaseAccessPage = lazy(() => import('./Admin/UsersPage/UserDatabaseAccessPage'))
const UserRolesPage = lazy(() => import('./Admin/UserRoles/UserRolesPage'))
const UsersPage = lazy(() => import('./Admin/UsersPage/UsersPage'))
const WallplotComposerPage = lazy(() => import('./WellPages/WallplotComposer/WallplotComposerPage'))
const WallplotTemplatesPage = lazy(() => import('components/WellPages/WallplotComposer/WallplotTemplatesPage'))
const WebsiteFileUploadsPage = lazy(() => import('./Admin/WebsiteFileUploads/WebsiteFileUploadsPage'))
const WellDetailsPage = lazy(() => import('components/WellPages/WellDetails/WellDetailsPage'))
const WellListPage = lazy(() => import('./WellList/WellListPage'))
const ParametersRoadmapPage = lazy(() => import('./WellPages/ParametersRoadmap/ParametersRoadmapPage'))
const WellSeekerFeatureUsagePage = lazy(
  () => import('components/Admin/WellSeeker/WellSeekerFeatureUsage/WellSeekerFeatureUsagePage'),
)
const WellSeekerLicenseUploadPage = lazy(
  () => import('./Admin/WellSeeker/WellSeekerFileUploads/WellSeekerLicenseUploadsPage'),
)
const WellSeekerMagneticModelsUploadPage = lazy(
  () => import('./Admin/WellSeeker/WellSeekerFileUploads/WellSeekerMagneticModelsUploadPage'),
)
const WellSeekerReportCoversUploadPage = lazy(
  () => import('./Admin/WellSeeker/WellSeekerFileUploads/WellSeekerReportCoversUploadsPage'),
)
const WellSeekerReportTemplatesUploadPage = lazy(
  () => import('./Admin/WellSeeker/WellSeekerFileUploads/WellSeekerReportTemplatesUploadsPage'),
)
const WellSeekerUnitSetsUploadPage = lazy(
  () => import('./Admin/WellSeeker/WellSeekerFileUploads/WellSeekerUnitSetsUploadsPage'),
)
const WellSeekerUpdateConfigPage = lazy(
  () => import('./Admin/WellSeeker/WellSeekerUpdateConfig/WellSeekerUpdateConfigPage'),
)
const WellSeekerUpdateFilesPage = lazy(
  () => import('./Admin/WellSeeker/WellSeekerUpdateFiles/WellSeekerUpdateFilesPage'),
)
const WellSeekerUsagePage = lazy(() => import('components/Admin/WellSeeker/WellSeekerUsage/WellSeekerUsagePage'))
const WellViewPage = lazy(() => import('./Peloton/WellViewPage'))
const WitsmlServerPage = lazy(() => import('./DataAcquisition/WitsmlServers/WitsmlServerPage'))
const WitsTemplatesPage = lazy(() => import('./DataAcquisition/WitsTemplatesPage'))
const WellPlanningPage = lazy(() => import('./common/WellPlanningPage/WellPlanningPage'))

ChartJS.register(
  CategoryScale,
  LinearScale,
  TimeScale,
  ArcElement,
  BarElement,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Title,
  BoxAndWiskers,
  BoxPlotController,
)
ChartJS.register(zoomPlugin, annotationPlugin)
ChartJS.register(
  GradientPlugIn,
  dataLabelPlugin,
  chartMeasurePlugin,
  fillBetweenLinesPlugin,
  CenterTextPlugIn,
  customAnnotation,
  shadingAreaPluginX,
  ChartBackgroundPlugIn,
  chartDatasetSyncPlugin,
)

const publicRoutes = [
  {
    path: ROUTES.home.url,
    exact: true,
    Component: LandingPage,
  },
  {
    path: ROUTES.pelotonLanding.url,
    exact: true,
    Component: PelotonLanding,
  },
  {
    path: ROUTES.wellView.url,
    exact: true,
    Component: WellViewPage,
  },
  {
    path: ROUTES.loginCallback.url,
    Component: LoginCallBack,
  },
]

const privateRoutes = [
  {
    path: ROUTES.wellList.url,
    Component: WellListPage,
  },
  {
    path: ROUTES.acDashboard.url,
    Component: AntiCollisionDashPage,
  },
  {
    path: ROUTES.adminAcDashboardStatus.url,
    Component: AntiCollisionDashStatusPage,
  },
  {
    path: ROUTES.endpointUsage.url,
    Component: EndpointUsagePage,
  },
  {
    path: ROUTES.multiDbWellList.url,
    Component: MultiDbWellListPage,
  },
  {
    path: ROUTES.assetSearch.url,
    Component: AssetSearchPage,
  },
  {
    path: ROUTES.incidentDashboard.url,
    Component: IncidentDashboardPage,
  },
  {
    path: ROUTES.analytics.url,
    Component: AnalyticsPage,
  },
  {
    path: ROUTES.bids.url,
    Component: BidsPage,
  },
  {
    path: ROUTES.rigNames.url,
    Component: RigNamesPage,
  },
  {
    path: ROUTES.adminDataQc.url,
    Component: DataQcPage,
  },
  {
    path: ROUTES.objectQc.url,
    Component: ObjectValidationPage,
  },
  {
    path: ROUTES.invoiceQc.url,
    Component: InvoiceQcPage,
  },
  {
    path: ROUTES.shippingAddresses.url,
    Component: ShippingAddressesPage,
  },
  {
    path: ROUTES.shippingTickets.url,
    Component: ShippingTicketPage,
  },
  {
    path: ROUTES.pipeTallys.url,
    Component: PipeTallyPage,
  },
  {
    path: ROUTES.connectionAnalysis.url,
    Component: ConnectionAnalysisPage,
  },
  {
    path: ROUTES.crs.url,
    Component: CRSPage,
  },
  {
    path: ROUTES.personnelNames.url,
    Component: PersonnelNamesPage,
  },
  {
    path: ROUTES.personnelUtilization.url,
    Component: PersonnelUtilizationPage,
  },
  {
    path: ROUTES.auditData.url,
    Component: AuditDataPage,
  },
  {
    path: ROUTES.databaseRestoreStatus.url,
    Component: DatabaseRestoreStatusPage,
  },
  {
    path: ROUTES.databaseBackupFiles.url,
    Component: DatabaseBackupFilesPage,
  },
  {
    path: ROUTES.innovaAdminUsers.url,
    Component: InnovaAdminUsersPage,
  },
  {
    path: ROUTES.licenseDeactivations.url,
    Component: InnovaLicenseDeactivationsPage,
  },
  {
    path: ROUTES.innovaCompanys.url,
    Component: InnovaCompanysPage,
  },
  {
    path: ROUTES.innovaContacts.url,
    Component: InnovaContactsPage,
  },
  {
    path: ROUTES.innovaSoftwareDetails.url,
    Component: InnovaSoftwareDetailsPage,
  },
  {
    path: ROUTES.innovaSoftwareBilling.url,
    Component: InnovaSoftwareBillingPage,
  },
  {
    path: ROUTES.licenses.url,
    Component: InnovaLicensingPage,
  },
  {
    path: ROUTES.licenseRequest.url,
    Component: InnovaLicenseRequestPage,
  },
  {
    path: ROUTES.innovaInvoices.url,
    Component: InnovaInvoicesPage,
  },
  {
    path: ROUTES.innovaQuotes.url,
    Component: InnovaQuotesPage,
  },
  {
    path: ROUTES.wellDetails.url,
    Component: WellDetailsPage,
  },
  {
    path: ROUTES.antiCollision.url,
    Component: AntiCollisionPage,
  },
  {
    path: ROUTES.dailyReport.url,
    Component: DailyReportsPage,
  },
  {
    path: ROUTES.inventory.url,
    Component: InventoryPage,
  },
  {
    path: ROUTES.surveys.url,
    Component: SurveysPage,
  },
  {
    path: ROUTES.dailyCost.url,
    Component: DailyCostPage,
  },
  {
    path: ROUTES.parametersRoadmap.url,
    Component: ParametersRoadmapPage,
  },
  {
    path: ROUTES.drillString.url,
    Component: DrillStringPage,
  },
  {
    path: ROUTES.engineering.url,
    Component: EngineeringPage,
  },
  {
    path: ROUTES.wallplotComposer.url,
    Component: WallplotComposerPage,
  },
  {
    path: ROUTES.edr.url,
    Component: EdrPage,
  },
  {
    path: ROUTES.wellInsights.url,
    Component: WellInsightsPage,
  },
  {
    path: ROUTES.logistics.url,
    Component: LogisticsPage,
  },
  {
    path: ROUTES.units.url,
    Component: UnitsPage,
  },
  {
    path: ROUTES.users.url,
    Component: UsersPage,
  },
  {
    path: ROUTES.userRoles.url,
    Component: UserRolesPage,
  },
  {
    path: ROUTES.userDatabaseAccess.url,
    Component: UserDatabaseAccessPage,
  },
  {
    path: ROUTES.organizations.url,
    Component: OrganizationsPage,
  },
  {
    path: ROUTES.infrastructureDashboard.url,
    Component: InfrastructureDashboardPage,
  },
  {
    path: ROUTES.icdsAdmin.url,
    Component: IcdsAdminPage,
  },
  {
    path: ROUTES.wellSeekerAdmin.url,
    Component: WellSeekerUsagePage,
  },
  {
    path: ROUTES.wellSeekerUpdateFiles.url,
    Component: WellSeekerUpdateFilesPage,
  },
  {
    path: ROUTES.wellSeekerMagneticModelsUpload.url,
    Component: WellSeekerMagneticModelsUploadPage,
  },
  {
    path: ROUTES.wellSeekerReportTemplatesUpload.url,
    Component: WellSeekerReportTemplatesUploadPage,
  },
  {
    path: ROUTES.wellSeekerUnitSetsUpload.url,
    Component: WellSeekerUnitSetsUploadPage,
  },
  {
    path: ROUTES.wellSeekerLicenseUploads.url,
    Component: WellSeekerLicenseUploadPage,
  },
  {
    path: ROUTES.wellSeekerReportCoversUpload.url,
    Component: WellSeekerReportCoversUploadPage,
  },
  {
    path: ROUTES.wellSeekerUpdateConfig.url,
    Component: WellSeekerUpdateConfigPage,
  },
  {
    path: ROUTES.wellSeekerFeatureUsage.url,
    Component: WellSeekerFeatureUsagePage,
  },
  {
    path: ROUTES.errorModels.url,
    Component: ErrorModelsPage,
  },
  {
    path: ROUTES.witsmlServers.url,
    Component: WitsmlServerPage,
  },
  {
    path: ROUTES.witsTemplates.url,
    Component: WitsTemplatesPage,
  },
  {
    path: ROUTES.dataAckFileHistory.url,
    Component: DataAckFileStatusPage,
  },
  {
    path: ROUTES.databaseTree.url,
    Component: DatabaseTreePage,
  },
  {
    path: ROUTES.dataMigration.url,
    Component: DataMigrationPage,
  },
  {
    path: ROUTES.bankAccounts.url,
    Component: BankAccountsPage,
  },
  {
    path: ROUTES.innovaVendors.url,
    Component: InnovaVendorsPage,
  },
  {
    path: ROUTES.integrations.url,
    Component: IntegrationsPage,
  },
  {
    path: ROUTES.componentCatalog.url,
    Component: ComponentCatalogPage,
  },
  {
    path: ROUTES.centralizerCatalog.url,
    Component: CentralizerCatalogPage,
  },
  {
    path: ROUTES.stopCollarCatalog.url,
    Component: StopCollarCatalogPage,
  },
  {
    path: ROUTES.mwdProbeCatalog.url,
    Component: MwdProbeCatalogPage,
  },
  {
    path: ROUTES.bulkEdit.url,
    Component: BulkEditPage,
  },
  {
    path: ROUTES.fileManager.url,
    Component: FileManager,
  },
  {
    path: ROUTES.databaseChanges.url,
    Component: DatabaseChangesPage,
  },
  {
    path: ROUTES.wallplotTemplates.url,
    Component: WallplotTemplatesPage,
  },
  {
    path: ROUTES.websiteFileUploads.url,
    Component: WebsiteFileUploadsPage,
  },
  {
    path: ROUTES.resetPassword.url,
    Component: ResetPasswordPage,
  },
  {
    path: ROUTES.apiConfig.url,
    Component: ApiConfigPage,
  },
  {
    path: ROUTES.wellPlanning.url,
    Component: WellPlanningPage,
  }
]

//Google analytics
const MEASUREMENT_ID = 'G-W7H2RW01R9'
ReactGA.initialize(MEASUREMENT_ID)

// AppShell ensures the user is authorized, roleId is set and databaseOrg is set before rendering the main components.
const AppShell = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth0()
  const { user, userRole, databaseOrg } = useInnovaAuth()

  const isReady =
    !isLoading &&
    isAuthenticated &&
    typeof user?.name === 'string' &&
    user.name.trim() !== '' &&
    typeof userRole?.id === 'number' &&
    userRole.id > 0 &&
    typeof databaseOrg === 'string' &&
    databaseOrg !== ''

  if (!isReady) {
    return (
      <CircularProgress
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
        }}
      />
    )
  }

  return <>{children}</>
}

const Main = () => {
  const { theme } = useInnovaTheme()

  return (
    <div className='App'>
      <ThemeProvider theme={theme === 'dark' ? darkTheme : lightTheme}>
        <CssBaseline />
        <Suspense
          fallback={
            <div
              style={{
                position: 'fixed',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(0,0,0,0.25)',
                backdropFilter: 'blur(6px)',
                WebkitBackdropFilter: 'blur(6px)',
                zIndex: 2000,
              }}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 18,
                  padding: '32px 40px',
                  borderRadius: 14,
                  background: 'rgba(20,20,20,0.85)',
                  boxShadow: '0 12px 40px rgba(0,0,0,0.45)',
                  color: '#fff',
                  minWidth: 220,
                }}>
                <div
                  style={{
                    position: 'relative',
                    width: 200,
                    height: 200,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                  {/* Logo */}
                  <img
                    src={innovaLogo}
                    alt='Innova'
                    style={{
                      width: 200,
                      height: 200,
                      objectFit: 'contain',
                      animation: 'innovaBreath 2.2s ease-in-out infinite',
                      filter: 'drop-shadow(0 0 6px rgba(0,180,255,0.5))',
                    }}
                  />
                </div>

                {/* Loading text */}
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 500,
                    opacity: 0.9,
                    letterSpacing: 0.4,
                  }}>
                  Loading…
                </div>

                {/* Subtle animated dots */}
                <div
                  style={{
                    fontSize: 12,
                    opacity: 0.6,
                    letterSpacing: 2,
                    animation: 'innovaDots 1.5s infinite',
                  }}>
                  ● ● ●
                </div>
              </div>

              {/* Keyframes */}
              <style>
                {`
          @keyframes innovaBreath {
            0%, 100% {
              transform: scale(0.95);
            }
            50% {
              transform: scale(1.05);
            }
          }

          @keyframes innovaDots {
            0% { opacity: 0.2; }
            50% { opacity: 1; }
            100% { opacity: 0.2; }
          }
        `}
              </style>
            </div>
          }>
          <MainRouter publicRoutes={publicRoutes} privateRoutes={privateRoutes}>
            <AppShell>
              <ChatProvider>
                <NavBar />
              </ChatProvider>
              <ActionBar />
            </AppShell>
          </MainRouter>
        </Suspense>
      </ThemeProvider>
    </div>
  )
}

ModuleRegistry.registerModules([AllEnterpriseModule.with(AgChartsEnterpriseModule)])
LicenseManager.setLicenseKey(
  'Using_this_{AG_Charts_and_AG_Grid}_Enterprise_key_{AG-116125}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Innova_Drilling_&_Intervention}_is_granted_a_{Multiple_Applications}_Developer_License_for_{1}_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_need_to_be_licensed_in_addition_to_the_ones_working_with_{AG_Charts_and_AG_Grid}_Enterprise___This_key_has_been_granted_a_Deployment_License_Add-on_for_{1}_Production_Environment___This_key_works_with_{AG_Charts_and_AG_Grid}_Enterprise_versions_released_before_{17_December_2026}____[v3]_[0102]_MTc5NzQ2NTYwMDAwMA==8bec02c96675467c975ee658468586a4',
)
// legacy theme for now
provideGlobalGridOptions({
  theme: 'legacy',
})

const App = () => {
  return (
    <RecoilRoot>
      <ProvideAuth>
        <StyledEngineProvider injectFirst>
          <AppSettingsProvider>
            <WellListProvider>
              <Main />
            </WellListProvider>
          </AppSettingsProvider>
        </StyledEngineProvider>
      </ProvideAuth>
    </RecoilRoot>
  )
}

export default App
